import { useEffect, useLayoutEffect, useRef, useState } from "react";

export function usePopover<T extends HTMLElement>(align: "left" | "right" = "left") {
	const rootRef = useRef<T>(null);
	const [open, setOpen] = useState(false);
	const [panel, panelRef] = useState<HTMLDivElement | null>(null);

	useLayoutEffect(() => {
		const root = rootRef.current;

		if (!open || panel === null || root === null) {
			return;
		}

		if (typeof panel.showPopover === "function") {
			panel.showPopover();
		}

		const position = () => {
			const anchor = root.getBoundingClientRect();
			const bounds = panel.getBoundingClientRect();
			const inset = 16;
			const gap = 6;
			const left = align === "right" ? anchor.right - bounds.width : anchor.left;
			const below = anchor.bottom + gap;
			const above = anchor.top - bounds.height - gap;
			const top = below + bounds.height <= window.innerHeight - inset ? below : above;

			panel.style.left = `${Math.max(inset, Math.min(left, document.documentElement.clientWidth - bounds.width - inset))}px`;
			panel.style.top = `${Math.max(inset, Math.min(top, window.innerHeight - bounds.height - inset))}px`;
		};

		position();

		const observer = new ResizeObserver(position);
		observer.observe(panel);
		window.addEventListener("resize", position);
		document.addEventListener("scroll", position, true);

		return () => {
			observer.disconnect();
			window.removeEventListener("resize", position);
			document.removeEventListener("scroll", position, true);
			if (panel.isConnected && typeof panel.hidePopover === "function") {
				panel.hidePopover();
			}
		};
	}, [open, panel, align]);

	useEffect(() => {
		if (!open) {
			return;
		}

		const handlePointerDown = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) {
				setOpen(false);
			}
		};

		const handleKeyDown = (event: KeyboardEvent) => {
			if (event.key === "Escape") {
				event.preventDefault();
				event.stopImmediatePropagation();
				setOpen(false);
				rootRef.current?.querySelector<HTMLElement>("button")?.focus();
			}
		};

		document.addEventListener("pointerdown", handlePointerDown);
		document.addEventListener("keydown", handleKeyDown, true);

		return () => {
			document.removeEventListener("pointerdown", handlePointerDown);
			document.removeEventListener("keydown", handleKeyDown, true);
		};
	}, [open]);

	return { open, setOpen, rootRef, panelRef };
}
